'use client';

import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { ImageIcon, Loader2, X, Zap } from 'lucide-react';
import Image from 'next/image';
import type React from 'react';
import { TOOLTIP_CLASSES } from './controls/ControlIcons';

interface GeneratorInputProps {
  model: string;
  isAuthenticated: boolean;
  selectedImage: string | null;
  setSelectedImage: (url: string | null) => void;
  selectedImages?: string[];
  setSelectedImages?: (urls: string[]) => void;
  setIsImageModalOpen: (open: boolean) => void;
  setIsSignupModalOpen: (open: boolean) => void;
  videoInput: string;
  setVideoInput: (input: string) => void;
  handleInput: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  contentEditableRef: React.RefObject<HTMLTextAreaElement | null>;
  containerRef: React.RefObject<HTMLDivElement | null>;
  showEncouragement: boolean;
  activeGenerations: Map<string, any>;
  estimatedCost: string | number | null;
  handleSubmit: () => void;
  isSubmitting: boolean;
  MAX_CHARS: number;
}

export function GeneratorInput({
  model,
  isAuthenticated,
  selectedImage,
  setSelectedImage,
  selectedImages = [],
  setSelectedImages,
  setIsImageModalOpen,
  setIsSignupModalOpen,
  videoInput,
  handleInput,
  contentEditableRef,
  containerRef,
  showEncouragement,
  activeGenerations,
  estimatedCost,
  handleSubmit,
  isSubmitting,
  MAX_CHARS,
}: GeneratorInputProps) {
  return (
    <div className="input-section w-full px-3 pt-[12px] pb-[12px]" ref={containerRef}>
      <div className="input-row relative mb-0 flex w-full flex-row items-stretch justify-start gap-[12px]">
        {/* Assembly Line: Image + Divider + Textarea */}
        <div
          className="flex flex-grow flex-row items-start justify-start gap-[12px] overflow-visible"
          style={{ background: 'transparent' }}
        >
          {/* Upload Btn */}
          {model !== 'elevenlabs-sfx' && model !== 'z-image' && (
            <div className="relative flex flex-shrink-0 items-start justify-start gap-2 self-start overflow-visible">
              {/* For Nano Banana 2, show multiple images */}
              {model === 'nano-banana-2' ? (
                <div className="flex flex-col gap-2">
                  <div className="custom-scrollbar flex max-h-[340px] flex-col gap-2 overflow-y-auto overflow-x-visible pr-1">
                    {selectedImages.map((img, idx) => (
                      <div
                        key={idx}
                        className="upload-btn group relative flex-shrink-0 overflow-visible border-0 bg-transparent p-0 hover:transform-none hover:bg-transparent"
                      >
                        <div className="relative h-full w-full overflow-hidden rounded-[12px]">
                          <Image
                            src={img}
                            alt={`Selected image ${idx + 1}`}
                            fill
                            sizes="40px"
                            className="cursor-pointer object-cover transition-opacity hover:opacity-80"
                            onClick={() => setIsImageModalOpen(true)}
                          />
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (setSelectedImages) {
                              setSelectedImages(selectedImages.filter((_, i) => i !== idx));
                            }
                          }}
                          aria-label="Remove image"
                          className="-top-1.5 -right-1.5 absolute z-10 flex h-5 w-5 cursor-pointer items-center justify-center rounded-full bg-indigo-500 p-0.5 text-white opacity-0 shadow-md transition-opacity group-hover:opacity-100"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                    {selectedImages.length < 14 && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            className="upload-btn flex flex-col items-center justify-center gap-1 border-dashed"
                            aria-label="Add more images"
                            onClick={() => {
                              if (!isAuthenticated) {
                                setIsSignupModalOpen(true);
                                return;
                              }
                              setIsImageModalOpen(true);
                            }}
                          >
                            <ImageIcon className="h-6 w-6" />
                            {selectedImages.length > 0 && (
                              <span className="font-bold text-[10px]">Add</span>
                            )}
                          </button>
                        </TooltipTrigger>
                        <TooltipContent
                          showArrow={false}
                          className={TOOLTIP_CLASSES}
                          sideOffset={8}
                        >
                          Add more images
                        </TooltipContent>
                      </Tooltip>
                    )}
                  </div>
                </div>
              ) : (
                /* Original single image logic for other models */
                <>
                  {selectedImage ? (
                    <div className="upload-btn group relative flex-shrink-0 overflow-visible border-0 bg-transparent p-0 hover:transform-none hover:bg-transparent">
                      <div className="relative h-full w-full overflow-hidden rounded-[12px]">
                        <Image
                          src={selectedImage}
                          alt="Selected input"
                          fill
                          sizes="40px"
                          className="cursor-pointer object-cover transition-opacity hover:opacity-80"
                          onClick={() => setIsImageModalOpen(true)}
                        />
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedImage(null);
                        }}
                        aria-label="Remove image"
                        className="-top-1.5 -right-1.5 absolute z-10 flex h-5 w-5 cursor-pointer items-center justify-center rounded-full bg-indigo-500 p-0.5 text-white opacity-0 shadow-md transition-opacity group-hover:opacity-100"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ) : (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          className="upload-btn"
                          aria-label="Start Frame"
                          onClick={() => {
                            if (!isAuthenticated) {
                              setIsSignupModalOpen(true);
                              return;
                            }
                            setIsImageModalOpen(true);
                          }}
                        >
                          <ImageIcon className="h-6 w-6" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent showArrow={false} className={TOOLTIP_CLASSES} sideOffset={8}>
                        Start Frame
                      </TooltipContent>
                    </Tooltip>
                  )}
                </>
              )}
            </div>
          )}

          {/* Input Element */}
          <div className="input-wrapper relative flex-grow self-stretch rounded-none border-0 transition-colors duration-200">
            <div className="glow-effect" />
            <div className="relative flex min-w-0 flex-1 flex-col gap-2">
              <textarea
                ref={contentEditableRef}
                className="video-input !rounded-none !border-0 !bg-transparent min-h-[64px] w-full resize-none pt-1 pr-0 pb-7 pl-1 text-[15px] leading-relaxed"
                aria-label="Video description"
                spellCheck="false"
                placeholder={
                  model === 'elevenlabs-sfx'
                    ? 'Describe the SFX for ElevenLabs...'
                    : model === 'nano-banana-2' || model === 'z-image'
                      ? `Describe your image for ${model === 'nano-banana-2' ? 'Nano Banana' : 'Z-Image'}...`
                      : selectedImage
                        ? `Describe how you want ${model} to animate this...`
                        : `Describe your video scene for ${model}...`
                }
                onInput={(e) => {
                  handleInput(e as any);
                  const target = e.currentTarget;
                  target.style.height = 'auto';
                  const newHeight = target.scrollHeight;
                  target.style.height = `${newHeight}px`;
                  // Sync parent height if needed
                  if (target.parentElement) {
                    target.parentElement.style.height = 'auto';
                  }
                }}
                value={videoInput}
              />
              {videoInput.length > MAX_CHARS && (
                <div className="-bottom-5 absolute right-0 text-red-500 text-xs">
                  -{videoInput.length - MAX_CHARS} chars
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Generate Column */}
        <div className="relative flex min-w-0 flex-shrink-0 flex-col items-center justify-end self-end pt-1">
          <div className="pointer-events-none mb-1.5 flex h-4 items-center justify-center gap-1 whitespace-nowrap text-[#d1d5db] opacity-80 md:mb-2 md:h-5">
            {estimatedCost !== null ? (
              <>
                <Zap className="h-3 w-3 fill-current md:h-3.5 md:w-3.5" />
                <span className="font-bold text-[10px] uppercase tracking-widest md:text-[11px]">
                  {estimatedCost}
                </span>
              </>
            ) : (
              <div className="shimmer-dark inline-block h-4 w-12 rounded-full opacity-60 shadow-inner md:h-4.5" />
            )}
          </div>

          {/* Generate Button */}
          <button
            className="generate-btn flex w-full items-center justify-center"
            onClick={(e) => {
              e.preventDefault();
              if (!videoInput.trim()) {
                contentEditableRef.current?.focus();
                return;
              }
              handleSubmit();
            }}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <Loader2 className="h-5 w-5 animate-spin text-white" />
            ) : (
              <span>Generate</span>
            )}
          </button>
        </div>
      </div>
      {showEncouragement && activeGenerations.size > 0 && (
        <div className="flex w-full animate-soft-pulse items-center justify-center gap-2 pt-2 font-semibold text-[10px] text-emerald-400 drop-shadow-[0_2px_4px_rgba(0,0,0,0.5)] sm:text-xs md:text-base">
          <Zap className="h-4 w-4 md:h-5 md:w-5" />
          <span>Generate more while you wait</span>
        </div>
      )}
    </div>
  );
}
